import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PersonIcon from "@mui/icons-material/Person";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Avatar from "@mui/material/Avatar";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import useSnackbar from "../../hooks/useSnackbar";
import { matrixClient, MatrixMemberProfile } from "../../services/matrixClient";

export default function ClubChatMembers() {
	const { chatId, clubId } = useParams<{ chatId: string; clubId: string }>();
	const [loading, setLoading] = useState(false);
	const [roomName, setRoomName] = useState<string>("");
	const [members, setMembers] = useState<MatrixMemberProfile[]>([]);
	const [banDialogOpen, setBanDialogOpen] = useState(false);
	const [banTarget, setBanTarget] = useState<MatrixMemberProfile | null>(null);
	const [banReason, setBanReason] = useState("");
	const [actionLoading, setActionLoading] = useState(false);
	const navigate = useNavigate();
	const openSnackbar = useSnackbar();

	const activeMembers = members.filter((m) => m.membership === "join");
	const bannedMembers = members.filter((m) => m.membership === "ban");

	const loadData = async () => {
		if (!chatId) return;
		try {
			setLoading(true);
			const [roomInfo, memberProfiles] = await Promise.all([
				matrixClient.getRoomInfo(chatId),
				matrixClient.getMemberProfiles(chatId),
			]);
			setRoomName(roomInfo.name || chatId);
			setMembers(memberProfiles);
		} catch (error) {
			console.error("Failed to load members:", error);
			openSnackbar("Error loading room members");
		}
		setLoading(false);
	};

	useEffect(() => {
		loadData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [chatId]);

	const handleBanClick = (member: MatrixMemberProfile) => {
		setBanTarget(member);
		setBanReason("");
		setBanDialogOpen(true);
	};

	const handleBanConfirm = async () => {
		if (!banTarget || !chatId) return;
		try {
			setActionLoading(true);
			const userId = banTarget.bridgesUserId || banTarget.matrixUserId;
			await matrixClient.banUser(chatId, userId, banReason || undefined);
			openSnackbar(`${banTarget.displayName} banned from chat`);
			setBanDialogOpen(false);
			setBanTarget(null);
			loadData();
		} catch (error) {
			console.error("Ban failed:", error);
			openSnackbar("Error banning user");
		}
		setActionLoading(false);
	};

	const handleUnban = async (member: MatrixMemberProfile) => {
		if (!chatId) return;
		try {
			setActionLoading(true);
			const userId = member.bridgesUserId || member.matrixUserId;
			await matrixClient.unbanUser(chatId, userId);
			openSnackbar(`${member.displayName} unbanned`);
			loadData();
		} catch (error) {
			console.error("Unban failed:", error);
			openSnackbar("Error unbanning user");
		}
		setActionLoading(false);
	};

	const handleViewProfile = (member: MatrixMemberProfile) => {
		if (member.bridgesUserId) {
			navigate(`/user/${member.bridgesUserId}`);
		}
	};

	const getAvatarSrc = (member: MatrixMemberProfile) => {
		if (!member.imageLink) return undefined;
		if (member.imageLink.startsWith("mxc://")) {
			return matrixClient.getMediaUrl(member.imageLink);
		}
		return member.imageLink;
	};

	return (
		<Paper sx={{ p: 2, display: "flex", flexDirection: "column" }}>
			<Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
				<IconButton
					onClick={() => navigate(`/club/${clubId}/chat/${encodeURIComponent(chatId || "")}`)}
					sx={{ mr: 1 }}
					size="small"
				>
					<ArrowBackIcon />
				</IconButton>
				<Typography component="h2" variant="h6" color="primary">
					{roomName} — Members
				</Typography>
				<Chip
					label={`${activeMembers.length} active`}
					size="small"
					color="success"
					sx={{ ml: 2, height: 20, fontSize: "0.7rem" }}
				/>
				{bannedMembers.length > 0 && (
					<Chip
						label={`${bannedMembers.length} banned`}
						size="small"
						color="error"
						sx={{ ml: 1, height: 20, fontSize: "0.7rem" }}
					/>
				)}
			</Box>

			{loading && (
				<Box sx={{ width: "100%", my: 0.5 }}>
					<LinearProgress />
				</Box>
			)}

			{/* Active Members */}
			<Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
				Active Members
			</Typography>
			<TableContainer>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>User</TableCell>
							<TableCell>Bridges ID</TableCell>
							<TableCell align="right">Actions</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{activeMembers.map((member) => (
							<TableRow key={member.matrixUserId}>
								<TableCell>
									<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										<Avatar
											src={getAvatarSrc(member)}
											sx={{ width: 32, height: 32 }}
										>
											{member.displayName?.charAt(0) || "?"}
										</Avatar>
										<Box>
											<Typography variant="body2" sx={{ fontWeight: 500 }}>
												{member.displayName}
											</Typography>
											<Typography variant="caption" sx={{ color: "#999" }}>
												{member.matrixUserId}
											</Typography>
										</Box>
									</Box>
								</TableCell>
								<TableCell>
									<Typography variant="body2" sx={{ color: "#666" }}>
										{member.bridgesUserId || "-"}
									</Typography>
								</TableCell>
								<TableCell align="right">
									<Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}>
										{member.bridgesUserId && (
											<Tooltip title="View Profile">
												<IconButton
													size="small"
													onClick={() => handleViewProfile(member)}
												>
													<PersonIcon fontSize="small" />
												</IconButton>
											</Tooltip>
										)}
										<Tooltip title="Ban from chat">
											<IconButton
												size="small"
												color="error"
												onClick={() => handleBanClick(member)}
												disabled={actionLoading}
											>
												<BlockIcon fontSize="small" />
											</IconButton>
										</Tooltip>
									</Box>
								</TableCell>
							</TableRow>
						))}
						{activeMembers.length === 0 && !loading && (
							<TableRow>
								<TableCell colSpan={3} sx={{ textAlign: "center", color: "#999" }}>
									No active members
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</TableContainer>

			{/* Banned Members */}
			{bannedMembers.length > 0 && (
				<>
					<Typography variant="subtitle2" sx={{ fontWeight: 600, mt: 3, mb: 1, color: "error.main" }}>
						Banned Users
					</Typography>
					<TableContainer>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>User</TableCell>
									<TableCell>Bridges ID</TableCell>
									<TableCell align="right">Actions</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{bannedMembers.map((member) => (
									<TableRow key={member.matrixUserId} sx={{ bgcolor: "#fff5f5" }}>
										<TableCell>
											<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
												<Avatar
													src={getAvatarSrc(member)}
													sx={{ width: 32, height: 32, opacity: 0.5 }}
												>
													{member.displayName?.charAt(0) || "?"}
												</Avatar>
												<Box>
													<Typography variant="body2" sx={{ fontWeight: 500, color: "#999" }}>
														{member.displayName}
													</Typography>
													<Typography variant="caption" sx={{ color: "#ccc" }}>
														{member.matrixUserId}
													</Typography>
												</Box>
											</Box>
										</TableCell>
										<TableCell>
											<Typography variant="body2" sx={{ color: "#999" }}>
												{member.bridgesUserId || "-"}
											</Typography>
										</TableCell>
										<TableCell align="right">
											<Tooltip title="Unban">
												<IconButton
													size="small"
													color="success"
													onClick={() => handleUnban(member)}
													disabled={actionLoading}
												>
													<CheckCircleIcon fontSize="small" />
												</IconButton>
											</Tooltip>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</TableContainer>
				</>
			)}

			<Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
				<Button
					variant="outlined"
					onClick={() => loadData()}
					disabled={loading}
				>
					Refresh
				</Button>
			</Box>

			{/* Ban Confirmation Dialog */}
			<Dialog open={banDialogOpen} onClose={() => setBanDialogOpen(false)} maxWidth="sm" fullWidth>
				<DialogTitle>
					Ban {banTarget?.displayName} from chat?
				</DialogTitle>
				<DialogContent>
					<Typography variant="body2" sx={{ mb: 2, color: "#666" }}>
						This user will be removed from the chat and won't be able to rejoin until unbanned.
					</Typography>
					<TextField
						fullWidth
						label="Reason (optional)"
						value={banReason}
						onChange={(e) => setBanReason(e.target.value)}
						multiline
						rows={2}
						variant="outlined"
						size="small"
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setBanDialogOpen(false)} disabled={actionLoading}>
						Cancel
					</Button>
					<Button
						onClick={handleBanConfirm}
						variant="contained"
						color="error"
						disabled={actionLoading}
					>
						Ban User
					</Button>
				</DialogActions>
			</Dialog>
		</Paper>
	);
}
